import { MIXED_DELIVERY_MESSAGE } from "@/lib/self-schedule-virtual-services";
import { toast } from "@/lib/use-toast";

import { ServiceVisibilityType } from "@/types/enums/service";
import { PublicServiceDetail } from "@/types/service";

import { LocationType } from "@/stores/self-schedule-store";

export interface SelectionDeps {
  addSelectedService: (
    serviceId: string,
    serviceName?: string,
  ) => string | void;
  /** Remove every selected instance of the given service. */
  removeAllSelectedServiceInstances: (serviceId: string) => void;
  /** Service ids currently in the cart — duplicates included, one per instance. */
  selectedServiceIds: string[];
  numAllowedServices: number;
  onRequireMfa?: (
    serviceId: string,
    onSelect?: (serviceId: string) => void,
  ) => void;
  onPhoneOnlyService?: (serviceId: string) => void;
  /** The patient's Telehealth or In-Person choice, if they made one. */
  locationType?: LocationType | null;
  onSelected?: (serviceId: string) => void;
  onOpenSelectionsDrawer?: (serviceId: string) => void;
}

/** Tells the patient, and returns true, when `service` would put telehealth
 * and in-person services in one cart. A patient who chose Telehealth books at
 * a virtual location, where every visit is by video, so a clinic's old
 * telehealth services can go with its virtual ones there. */
export function refuseMixedDelivery(
  services: PublicServiceDetail[] | undefined,
  selectedServiceIds: string[],
  service: PublicServiceDetail,
  locationType?: LocationType | null,
): boolean {
  if (locationType === LocationType.VIRTUAL) return false;
  const mixes = selectedServiceIds.some(
    (id) =>
      Boolean(services?.find((s) => s.id === id)?.isVirtual) !==
      Boolean(service.isVirtual),
  );
  if (mixes) {
    toast({
      title: "Book this service separately",
      description: MIXED_DELIVERY_MESSAGE,
      variant: "warning",
    });
  }
  return mixes;
}

export function createSelectionHandler(
  services: PublicServiceDetail[] | undefined,
  deps: SelectionDeps,
) {
  const {
    addSelectedService,
    removeAllSelectedServiceInstances,
    selectedServiceIds,
    numAllowedServices,
    onPhoneOnlyService,
    locationType,
    onRequireMfa,
    onSelected,
    onOpenSelectionsDrawer,
  } = deps;

  const proceed = (service: PublicServiceDetail) => {
    // Both verified existing patients and unrestricted services use the same
    // drawer flow; selecting a service with add-ons waits for confirmation.
    if ((service.addons?.length || 0) > 0 && onOpenSelectionsDrawer) {
      onOpenSelectionsDrawer(service.id);
      return;
    }
    if (selectedServiceIds.length >= numAllowedServices) return;
    addSelectedService(service.id, service.name);
    onSelected?.(service.id);
  };

  return (serviceId: string, intent: "toggle" | "increment" = "toggle") => {
    const service = services?.find((s) => s.id === serviceId);
    if (!service) return;
    if (intent === "toggle" && selectedServiceIds.includes(service.id)) {
      removeAllSelectedServiceInstances(service.id);
      return;
    }
    if (service.onlyBookableOverThePhone && onPhoneOnlyService) {
      onPhoneOnlyService(serviceId);
      return;
    }
    if (
      refuseMixedDelivery(services, selectedServiceIds, service, locationType)
    )
      return;
    if (service.visibility === ServiceVisibilityType.EXISTING && onRequireMfa) {
      onRequireMfa(serviceId, () => proceed(service));
      return;
    }
    proceed(service);
  };
}
